import React, { useEffect, useMemo, useState } from "react";
import { ukDayYear } from "@/lib/time";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileSignature, FileDown, FileCheck2, Loader2, Search, PenLine, Clock, Building2, Handshake, Lock, ShieldCheck } from "lucide-react";

const fmt = (iso) => ukDayYear(iso, "—");

const METHOD_META = {
  electronic: { label: "E-sign", Icon: PenLine, cls: "bg-blue-50 border-blue-200 text-blue-700" },
  wet_ink: { label: "Wet ink", Icon: FileCheck2, cls: "bg-teal-50 border-teal-200 text-teal-700" },
};

const KIND_META = {
  lender: { label: "Lender", Icon: Building2, cls: "bg-slate-100 border-slate-200 text-slate-700" },
  referrer: { label: "Introducer", Icon: Handshake, cls: "bg-indigo-50 border-indigo-200 text-indigo-700" },
};

const TABS = [
  { key: "all", label: "All" },
  { key: "executed", label: "Executed" },
  { key: "awaiting", label: "Awaiting signature" },
];

export default function HubAgreements() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/hub/agreements/archive")
      .then((r) => setRows(r.data.agreements || []))
      .catch(() => { setRows([]); toast.error("Could not load the agreement archive"); });
  }, []);

  const filtered = useMemo(() => {
    let out = rows || [];
    if (tab === "executed") out = out.filter((r) => r.status === "signed");
    if (tab === "awaiting") out = out.filter((r) => r.status !== "signed");
    const s = q.trim().toLowerCase();
    if (s) out = out.filter((r) => `${r.party_name} ${r.email} ${r.signed_name || ""}`.toLowerCase().includes(s));
    return out;
  }, [rows, tab, q]);

  const stats = useMemo(() => {
    const all = rows || [];
    return {
      executed: all.filter((r) => r.status === "signed").length,
      esign: all.filter((r) => r.method === "electronic").length,
      wet: all.filter((r) => r.method === "wet_ink").length,
      awaiting: all.filter((r) => r.status !== "signed").length,
    };
  }, [rows]);

  const blobDl = async (key, path, fname) => {
    setBusy(key);
    try {
      const r = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("Download failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-6" data-testid="hub-agreements-page">
      <div>
        <div className="eyebrow">Compliance</div>
        <h1 className="t">Agreement archive</h1>
        <p className="lede">Every partner agreement in one place — dates, method and one-click downloads for audits.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: "Executed", value: stats.executed, Icon: FileSignature, tone: "text-teal-600" },
          { label: "E-signed", value: stats.esign, Icon: PenLine, tone: "text-blue-600" },
          { label: "Wet ink", value: stats.wet, Icon: FileCheck2, tone: "text-slate-700" },
          { label: "Awaiting", value: stats.awaiting, Icon: Clock, tone: "text-[#3B63A8]" },
        ].map(({ label, value, Icon, tone }) => (
          <div key={label} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`agreements-stat-${label.toLowerCase().replace(" ", "-")}`}>
            <div className="flex items-center gap-1.5 text-[10px] font-semibold tracking-[0.16em] uppercase text-slate-400">
              <Icon className={`size-3 ${tone}`} /> {label}
            </div>
            <div className="mt-1.5 text-[24px] font-semibold text-slate-900 tabular-nums">{rows ? value : "—"}</div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
                    className={`px-3 h-8 rounded-md text-[12.5px] font-semibold transition-colors ${tab === t.key ? "bg-[#0B1220] text-white" : "text-slate-500 hover:text-slate-900"}`}
                    data-testid={`agreements-tab-${t.key}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[220px] max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search partner, email, signatory…" className="pl-9 h-9 bg-white" data-testid="agreements-search" />
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {!rows ? (
          <div className="p-10 flex justify-center"><Loader2 className="size-5 animate-spin text-slate-400" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-[13px] text-slate-400" data-testid="agreements-empty">No agreements match.</div>
        ) : (
          <>
          {/* Phone: tap-friendly cards */}
          <div className="lg:hidden divide-y divide-slate-100" data-testid="agreements-cards">
            {filtered.map((r) => {
              const k = KIND_META[r.kind];
              const signed = r.status === "signed";
              return (
                <div key={`m-${r.kind}-${r.party_id}`} className="p-4" data-testid={`agreement-card-${r.party_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-slate-900 sm:truncate">{r.party_name}</span>
                        {r.version > 1 && (
                          <span className="rounded-full border border-slate-200 bg-slate-50 text-slate-500 px-1.5 h-4 inline-flex items-center text-[10px] font-semibold tabular-nums">v{r.version}</span>
                        )}
                      </div>
                      <div className="text-[11.5px] text-slate-400 truncate">{r.email}</div>
                    </div>
                    <span className={`inline-flex items-center gap-1 rounded-full border px-2 h-5 text-[10.5px] font-semibold shrink-0 ${k.cls}`}>
                      <k.Icon className="size-2.5" /> {k.label}
                    </span>
                  </div>
                  <div className="mt-2.5 flex items-center gap-1.5 flex-wrap">
                    {signed ? (
                      <span className="inline-flex items-center gap-1 rounded-full border border-teal-200 bg-teal-50 text-teal-700 px-2 h-5 text-[10.5px] font-semibold">
                        <ShieldCheck className="size-2.5" /> Executed {fmt(r.signed_at)}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full border border-[#7da9ff]/45 bg-[#7da9ff]/[0.10] text-[#3B63A8] px-2 h-5 text-[10.5px] font-semibold">
                        <Clock className="size-2.5" /> Awaiting signature
                      </span>
                    )}
                    {r.nda_status === "signed" && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-teal-200 bg-teal-50 text-teal-700 px-2 h-5 text-[10.5px] font-semibold">
                        <Lock className="size-2.5" /> NDA
                      </span>
                    )}
                    {r.signed_name && <span className="text-[11px] text-slate-400">by {r.signed_name}</span>}
                  </div>
                  <div className="mt-3 flex items-center gap-1.5">
                    <Button variant="outline" size="sm" className="h-9 rounded-lg flex-1"
                            disabled={busy === `pdf-${r.party_id}`}
                            onClick={() => blobDl(`pdf-${r.party_id}`, `/hub/agreements/${r.kind}/${r.party_id}.pdf`, `connexis-agreement-${(r.party_name || "partner").replace(/\s+/g, "-").toLowerCase()}.pdf`)}
                            data-testid={`agreement-m-dl-pdf-${r.party_id}`}>
                      {busy === `pdf-${r.party_id}` ? <Loader2 className="size-3.5 animate-spin" /> : <><FileDown className="size-3.5 mr-1" /> PDF</>}
                    </Button>
                    {r.has_scan && (
                      <Button variant="outline" size="sm" className="h-9 rounded-lg flex-1 text-teal-700 border-teal-200"
                              disabled={busy === `scan-${r.party_id}`}
                              onClick={() => blobDl(`scan-${r.party_id}`, `/hub/agreements/${r.kind}/${r.party_id}/scan`, `connexis-agreement-scan-${(r.party_name || "partner").replace(/\s+/g, "-").toLowerCase()}`)}
                              data-testid={`agreement-m-dl-scan-${r.party_id}`}>
                        {busy === `scan-${r.party_id}` ? <Loader2 className="size-3.5 animate-spin" /> : <><FileCheck2 className="size-3.5 mr-1" /> Scan</>}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="hidden lg:block tb-wrap">
            <table className="tb" data-testid="agreements-table">
              <thead>
                <tr>
                  {["Partner", "Type", "Method", "NDA", "Sent", "Executed", "Signed by", ""].map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const m = METHOD_META[r.method];
                  const k = KIND_META[r.kind];
                  const signed = r.status === "signed";
                  return (
                    <tr key={`${r.kind}-${r.party_id}`}  data-testid={`agreement-row-${r.party_id}`}>
                      <td>
                        <div className="flex items-center gap-1.5">
                          <span className="cell-main">{r.party_name}</span>
                          {r.version > 1 && (
                            <span className="rounded-full border border-slate-200 bg-slate-50 text-slate-500 px-1.5 h-4 inline-flex items-center text-[10px] font-semibold tabular-nums"
                                  title={`Re-issued — currently on version ${r.version}`} data-testid={`agreement-version-${r.party_id}`}>
                              v{r.version}
                            </span>
                          )}
                        </div>
                        <div className="cell-sub">{r.email}</div>
                      </td>
                      <td>
                        <span className={`inline-flex items-center gap-1 rounded-full border px-2 h-5 text-[10.5px] font-semibold ${k.cls}`}>
                          <k.Icon className="size-2.5" /> {k.label}
                        </span>
                      </td>
                      <td>
                        {signed && m ? (
                          <span className={`inline-flex items-center gap-1 rounded-full border px-2 h-5 text-[10.5px] font-semibold ${m.cls}`}>
                            <m.Icon className="size-2.5" /> {m.label}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full border border-[#7da9ff]/45 bg-[#7da9ff]/[0.10] text-[#3B63A8] px-2 h-5 text-[10.5px] font-semibold">
                            <Clock className="size-2.5" /> Awaiting
                          </span>
                        )}
                      </td>
                      <td className="whitespace-nowrap" data-testid={`agreement-nda-${r.party_id}`}>
                        {r.nda_status === "signed" ? (
                          <span className="inline-flex items-center gap-1 rounded-full border border-teal-200 bg-teal-50 text-teal-700 px-2 h-5 text-[10.5px] font-semibold">
                            <Lock className="size-2.5" /> Executed
                          </span>
                        ) : r.nda_status === "sent" ? (
                          <span className="inline-flex items-center gap-1 rounded-full border border-[#7da9ff]/45 bg-[#7da9ff]/[0.10] text-[#3B63A8] px-2 h-5 text-[10.5px] font-semibold">
                            <Clock className="size-2.5" /> Awaiting
                          </span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="text-slate-500 whitespace-nowrap">{fmt(r.sent_at)}</td>
                      <td className="whitespace-nowrap">
                        {signed ? <span className="font-semibold text-slate-900">{fmt(r.signed_at)}</span> : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="text-slate-600">{r.signed_name || "—"}</td>
                      <td>
                        <div className="flex items-center justify-end gap-1.5">
                          <Button variant="outline" size="sm" className="h-8 rounded-lg"
                                  disabled={busy === `pdf-${r.party_id}`}
                                  onClick={() => blobDl(`pdf-${r.party_id}`, `/hub/agreements/${r.kind}/${r.party_id}.pdf`, `connexis-agreement-${(r.party_name || "partner").replace(/\s+/g, "-").toLowerCase()}.pdf`)}
                                  data-testid={`agreement-dl-pdf-${r.party_id}`}>
                            {busy === `pdf-${r.party_id}` ? <Loader2 className="size-3.5 animate-spin" /> : <><FileDown className="size-3.5 mr-1" /> PDF</>}
                          </Button>
                          {r.has_scan && (
                            <Button variant="outline" size="sm" className="h-8 rounded-lg text-teal-700 border-teal-200 hover:bg-teal-50"
                                    disabled={busy === `scan-${r.party_id}`}
                                    onClick={() => blobDl(`scan-${r.party_id}`, `/hub/agreements/${r.kind}/${r.party_id}/scan`, `connexis-agreement-scan-${(r.party_name || "partner").replace(/\s+/g, "-").toLowerCase()}`)}
                                    data-testid={`agreement-dl-scan-${r.party_id}`}>
                              {busy === `scan-${r.party_id}` ? <Loader2 className="size-3.5 animate-spin" /> : <><FileCheck2 className="size-3.5 mr-1" /> Scan</>}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>
    </div>
  );
}
